import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@diggens/api-client';
import { useSession } from './useAuth';
import type { AgentRosterEntry, AgentRunEvents, AgentRunSummary, AgentStats, PlanApprovals, SubagentSlug, Tenant, TenantPersonaSummary } from '@diggens/types';

/** The tenant's specialists and whether each is recruited (admins only). */
export function useAgentRoster() {
  return useQuery({
    queryKey: ['agents', 'roster'],
    queryFn: () => apiFetch<AgentRosterEntry[]>('/agents/roster'),
  });
}

/** Recruit or dismiss a specialist for the caller's workspace. */
export function useRecruitAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agent, enabled }: { agent: SubagentSlug; enabled: boolean }) => apiFetch<AgentRosterEntry[]>(`/agents/roster/${agent}`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
    onSuccess: (roster) => {
      queryClient.setQueryData(['agents', 'roster'], roster);
    },
  });
}

/** Each agent's work over the last 7 days, for the team; polled every 15s. */
export function useAgentStats() {
  return useQuery({
    queryKey: ['agents', 'stats'],
    queryFn: () => apiFetch<AgentStats[]>('/agents/stats'),
    refetchInterval: 15_000,
    staleTime: 0,
  });
}

/** Give a recruited specialist a job; resolves with the run it runs as. */
export function useStartJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agent, task }: { agent: SubagentSlug; task: string }) => apiFetch<{ runId: string }>('/agents/jobs', { method: 'POST', body: JSON.stringify({ agent, task }) }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

/**
 * Every unfinished top-level run plus the 50 newest of the last 7 days, newest first. The unfinished ones are fetched
 * on their own so a run waiting days for approval never falls off a busy list. Polled every 5s while any is running.
 */
export function useAgentRuns({ poll = true }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: ['agents', 'runs', 'recent'],
    queryFn: async () => {
      const [active, recent] = await Promise.all([apiFetch<AgentRunSummary[]>('/agents/runs?scope=active'), apiFetch<AgentRunSummary[]>('/agents/runs?scope=recent')]);
      const byId = new Map([...recent, ...active].map((run) => [run.runId, run]));
      return [...byId.values()].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
    },
    refetchInterval: (query) => (poll ? (query.state.data?.some((run) => run.status === 'running') ? 5_000 : 30_000) : false),
    staleTime: 0,
  });
}

/** One run and every event of its tree, polled every 2s while it runs; a run paused on its plan is checked every 15s, since it may resume. */
export function useAgentRunEvents(runId: string | null) {
  return useQuery({
    queryKey: ['agents', 'run', runId],
    queryFn: () => apiFetch<AgentRunEvents>(`/agents/runs/${encodeURIComponent(runId!)}`),
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.run.status;
      return status === 'running' ? 2_000 : status === 'waiting' ? 15_000 : false;
    },
    staleTime: 0,
  });
}

/** Plans waiting for an admin, and recent decisions: one query, which Activity and the nav count each poll. */
const planApprovalsQuery = queryOptions({
  queryKey: ['agents', 'approvals'],
  queryFn: () => apiFetch<PlanApprovals>('/agents/approvals'),
  staleTime: 0,
});

/** Plans waiting for an admin, and recent decisions; polled every 3s unless `poll` is false, for a one-time look. */
export function usePlanApprovals({ poll = true }: { poll?: boolean } = {}) {
  return useQuery({ ...planApprovalsQuery, refetchInterval: poll ? 3_000 : false });
}

/**
 * How many plans wait for an admin, for the nav badge. It shares the approvals query, polled every 30s here; a page
 * showing the approvals polls it every 3s for as long as it is mounted.
 */
export function usePendingApprovalCount(enabled: boolean): number {
  const { data } = useQuery({ ...planApprovalsQuery, refetchInterval: 30_000, enabled, select: (approvals) => approvals.pending.length });
  return data ?? 0;
}

export function useDecidePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, decision, note }: { planId: string; decision: 'approve' | 'reject'; note?: string }) =>
      apiFetch<{ planId: string; decision: 'approved' | 'rejected' }>(`/agents/plans/${encodeURIComponent(planId)}/decide`, {
        method: 'POST',
        body: JSON.stringify({ decision, ...(note ? { note } : {}) }),
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

/** Super admin: a tenant's subagent roster. */
export function useTenantAgents(tenantId: string) {
  return useQuery({
    queryKey: ['tenant-agents', tenantId],
    queryFn: () => apiFetch<AgentRosterEntry[]>(`/admin/tenants/${encodeURIComponent(tenantId)}/agents`),
    enabled: !!tenantId,
  });
}

/** Super admin: switch one of a tenant's subagents on or off. */
export function useSetTenantAgent(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agent, enabled }: { agent: SubagentSlug; enabled: boolean }) =>
      apiFetch<AgentRosterEntry[]>(`/admin/tenants/${encodeURIComponent(tenantId)}/agents/${agent}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: (roster) => {
      queryClient.setQueryData(['tenant-agents', tenantId], roster);
    },
  });
}

/** How a persona's brief that a super admin ran now ended; of what its runner keeps, the ops sweep's count of new findings. */
export interface PersonaRunOutcome {
  status: 'succeeded' | 'failed' | 'skipped';
  runId: string | null;
  error: string | null;
  findings: { recorded?: number } | null;
}

/** Super admin: run one of a tenant's scheduled personas now, as its next firing would; the run then shows in the tenant's Activity. */
export function useRunTenantPersona(tenantId: string) {
  return useMutation({
    mutationFn: (slug: string) => apiFetch<PersonaRunOutcome>(`/admin/tenants/${encodeURIComponent(tenantId)}/personas/${encodeURIComponent(slug)}/run`, { method: 'POST' }),
  });
}

/** A Lantern workspace a super admin can see as Lantern staff. */
export interface LanternWorkspace {
  workspace_id: string;
  workspace_name: string;
  org_name: string;
}

/** Super admin: the Lantern workspaces they can see as Lantern staff, matching a name search or one id. */
export function useLanternWorkspaces({ search, workspaceId }: { search?: string; workspaceId?: string | null }, enabled = true) {
  // Lantern answers for whoever asks, and signing out leaves the query cache in place, so the list is cached per user
  const { data: user } = useSession();
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (workspaceId) params.set('workspace_id', workspaceId);
  const query = params.toString();

  return useQuery({
    queryKey: ['lantern-workspaces', user?.uid ?? null, search ?? '', workspaceId ?? null],
    queryFn: async () => (await apiFetch<{ workspaces: LanternWorkspace[] }>(`/admin/lantern-workspaces${query ? `?${query}` : ''}`)).workspaces,
    enabled: enabled && !!user,
    staleTime: 60_000,
    // A server without the directory answers 503 every time
    retry: false,
  });
}

/** Super admin: link a tenant to the Lantern workspace its onboarding operator reads, or unlink it with null. */
export function useSetTenantLanternWorkspace(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workspaceId: string | null) =>
      apiFetch<{ tenant_id: string; lantern_workspace_id: string | null }>(`/admin/tenants/${encodeURIComponent(tenantId)}/lantern-workspace`, {
        method: 'PUT',
        body: JSON.stringify({ workspace_id: workspaceId }),
      }),
    onSuccess: ({ lantern_workspace_id }) => {
      queryClient.setQueryData<Tenant>(['admin-tenant', tenantId], (tenant) => tenant && { ...tenant, lantern_workspace_id });
      // The onboarding operator is on the roster only while the tenant is linked
      return queryClient.invalidateQueries({ queryKey: ['tenant-agents', tenantId] });
    },
  });
}

/** Every version of every persona in the caller's workspace, drafts included (admins only). */
export function usePersonas() {
  return useQuery({
    queryKey: ['agents', 'personas'],
    queryFn: async () => (await apiFetch<{ personas: TenantPersonaSummary[] }>('/personas')).personas,
  });
}

/** Activate a persona version (a disabled one, or a draft an admin takes on directly), or disable the active one. */
export function useSetPersonaStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personaId, action }: { personaId: string; action: 'activate' | 'disable' }) => apiFetch<{ persona: { id: string; slug: string; version: number; status: string } }>(`/personas/${encodeURIComponent(personaId)}/${action}`, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}
